import ResumeItem from "@/common/components/ResumeItem";
import Table from "@/common/components/Table";
import { formatCurrency } from "@/common/utils/formatters";
import { useParams } from "react-router-dom";
import useBillingBill from "../../hooks/useBillingBill";
import { billingBillType } from "../../constants/billingBillType.constant";
import { companyTotalizerFeesColumns, companyTotalizerProductsColumns } from "@/features/privateCompanies/constants/companyTotalizerDefaultColumns";
import { CompanyMovementsTransformer } from "@/features/privateCompanies/services/movementsTransformer";
import { strategy as companyKind } from "@/features/companies/constants/kind.constant";
import { billingBillStatus } from "@/common/constants/statusBills.constants";
import ResumeBox from "@/common/components/ResumeBox";
import { AlertCircleIcon } from "lucide-react";
import { Tooltip } from "react-tooltip";

const ResumeTab = () => {
  const { billId } = useParams();

  const {
    data: bill,
    isPending: billIsPending,
  } = useBillingBill({ id: billId });

  const totalAmountResumeLabel = !!bill?.client_company ? "Valor a receber" : "Valor a pagar"
  const totalAmountResumeTextColor = !!bill?.client_company ? "text-green-400" : "text-red-400"

  return (
    <div className="flex flex-col gap-6 h-[calc(100vh-192px)] overflow-y-auto pr-1">
      {bill?.client_company && (
        <div className="flex gap-8">
          <div className="flex items-end gap-4">
            <ResumeItem
              label="Filial da empresa"
              value={bill?.branch_display_name || '--'}
            />
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <div className="flex items-end gap-4">
          <ResumeItem
            label="Status"
            value={(
              <div className="flex items-center gap-1.5">
                <p className="text-[18px] text-yellow-400">
                  {bill?.status ? billingBillStatus.find((item) => item.value === bill.status)?.label || '' : ''}
                </p>

                {bill?.rejected_by_company_at && (
                  <>
                    <AlertCircleIcon
                      strokeWidth={1.5}
                      className="text-red-500 size-5"
                      data-tooltip-id="bill-rejected-tooltip"
                    />

                    <Tooltip id="bill-rejected-tooltip" className="text-sm">
                      Fatura rejeitada
                    </Tooltip>
                  </>
                )}
              </div>
            )}
            className="text-yellow-400"
            valueDataTestid="status_resume_item"
            isLoading={billIsPending}
          />

          <ResumeItem
            label="Tipo de fatura"
            value={bill?.kind ? billingBillType[bill.kind] : '--'}
            isLoading={billIsPending}
          />

          {bill?.client_company && (
            <ResumeItem
              label="Tipo de empresa"
              value={companyKind[bill?.client_company?.kind as keyof typeof companyKind]}
              isLoading={billIsPending}
            />
          )}

          {bill?.rejected_by_service_reason && (
            <ResumeItem
              label="Motivo da Falha"
              value={bill?.rejected_by_service_reason}
              isLoading={billIsPending}
            />
          )}
        </div>

        <div className="flex items-end gap-4">
          {(bill?.kind === 'consumption' || bill?.kind === 'payment') && (
            <>
              <ResumeItem
                label="Consumo"
                value={formatCurrency(Number(bill?.movements_totalizer?.consumed_total) || 0)}
                isLoading={billIsPending}
              />

              <ResumeItem
                label="Descontos"
                value={formatCurrency(Number(bill?.movements_totalizer?.discount_total) || 0)}
                isLoading={billIsPending}
              />
            </>
          )}

          {(bill?.kind === 'payment') && (
            <>
              <ResumeItem
                label="Valor em tarifas"
                value={formatCurrency(Number(bill?.fee_total) || 0)}
                isLoading={billIsPending}
              />
            </>
          )}

          <ResumeItem
            label={totalAmountResumeLabel}
            labelIsLoading={billIsPending}
            value={formatCurrency(Number(bill?.total_amount) || 0)}
            className={totalAmountResumeTextColor}
            isLoading={billIsPending}
          />

          {
            bill?.kind !== "service" && !bill?.establishment_company && (bill?.grouping_mode !== 'by_product' && bill?.grouping_mode !== 'by_product_category') && (
              <>
                <ResumeItem
                  label="Rentabilidade"
                  labelIsLoading={billIsPending}
                  value={formatCurrency(Number(bill?.profitability) || 0)}
                  valueDataTestid="profitability-resume-item"
                  className={Number(bill?.profitability) > 0 ? "text-green-400" : "text-red-400"}
                  isLoading={billIsPending}
                />
              </>
            )
          }
        </div>
      </div>

      {bill?.rejected_by_company_reason && (
        <ResumeBox
          label="Motivo da rejeição"
          value={bill?.rejected_by_company_reason}
          boxClassName="bg-yellow-op-2"
        />
      )}

      <div className="space-y-2">
        {bill?.movements_totalizer?.product_movements?.length !== 0 && (
          <div className="flex flex-col gap-4">
            <h5>Produtos</h5>
            <Table
              rows={CompanyMovementsTransformer.productsTableTransformer()(bill?.movements_totalizer?.product_movements) || []}
              columns={companyTotalizerProductsColumns}
              isLoadingData={billIsPending}
              loadingRows={2}
              page={0}
              totalPages={0}
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        {bill?.movements_totalizer?.fees?.length !== 0 && (
          <div className="flex flex-col gap-4">
            <h5>Tarifas</h5>
            <Table
              className={(bill?.movements_totalizer?.fees?.length || 0) === 0 ? 'overflow-hidden [&>*]:-mt-20 pt-12 h-[240px]' : ''}
              rows={CompanyMovementsTransformer.feesTableTransformer()(bill?.movements_totalizer?.fees) || []}
              columns={companyTotalizerFeesColumns}
              isLoadingData={billIsPending}
              loadingRows={2}
              page={0}
              totalPages={0}
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        {bill?.movements_totalizer?.special_fees?.length !== 0 && (
          <div className="flex flex-col gap-4">
            <p>Tarifas Especiais</p>
            <Table
              className={(bill?.movements_totalizer?.special_fees?.length || 0) === 0 ? 'overflow-hidden [&>*]:-mt-20 pt-12 h-[240px]' : ''}
              rows={CompanyMovementsTransformer.feesTableTransformer()(bill?.movements_totalizer?.special_fees) || []}
              columns={companyTotalizerFeesColumns}
              isLoadingData={billIsPending}
              loadingRows={2}
              page={0}
              totalPages={0}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default ResumeTab;
